// data/languages/swahili/categories/grammaire.ts

import { GrammarCategory } from '../../../../types/types';

export const grammaire: GrammarCategory = {

  // ============================================================
  // UNITÉ 1 — PRONOMS, CLASSES NOMINALES & STRUCTURE
  // ============================================================

  'Pronoms sujets': {
    'Ni': {
      translation: "Je",
      translationEn: "I",
      explanation: "Préfixe sujet 1re personne du singulier — se combine avec le marqueur de temps.",
      explanationEn: "First-person singular subject prefix — combines with the tense marker.",
      example: "Ninasoma. (Je lis / Je suis en train de lire.)",
      exampleEn: "Ninasoma. (I am reading / I read.)"
    },
    'U': {
      translation: "Tu",
      translationEn: "You (singular)",
      explanation: "Préfixe sujet 2e personne du singulier.",
      explanationEn: "Second-person singular subject prefix.",
      example: "Unasoma. (Tu lis / Tu es en train de lire.)",
      exampleEn: "Unasoma. (You are reading / You read.)"
    },
    'A': {
      translation: "Il/Elle",
      translationEn: "He/She",
      explanation: "Préfixe sujet 3e personne du singulier pour les personnes.",
      explanationEn: "Third-person singular subject prefix for people.",
      example: "Anasoma. (Il/Elle lit / Il/Elle est en train de lire.)",
      exampleEn: "Anasoma. (He/She is reading / He/She reads.)"
    },
    'Tu': {
      translation: "Nous",
      translationEn: "We",
      explanation: "Préfixe sujet 1re personne du pluriel.",
      explanationEn: "First-person plural subject prefix.",
      example: "Tunasoma. (Nous lisons / Nous sommes en train de lire.)",
      exampleEn: "Tunasoma. (We are reading / We read.)"
    },
    'M': {
      translation: "Vous",
      translationEn: "You (plural)",
      explanation: "Préfixe sujet 2e personne du pluriel.",
      explanationEn: "Second-person plural subject prefix.",
      example: "Mnasoma. (Vous lisez / Vous êtes en train de lire.)",
      exampleEn: "Mnasoma. (You are reading / You read.)"
    },
    'Wa': {
      translation: "Ils/Elles",
      translationEn: "They",
      explanation: "Préfixe sujet 3e personne du pluriel pour les personnes.",
      explanationEn: "Third-person plural subject prefix for people.",
      example: "Wanasoma. (Ils/Elles lisent / Ils/Elles sont en train de lire.)",
      exampleEn: "Wanasoma. (They are reading / They read.)"
    },
  },

  'Pronoms emphatiques': {
    'Mimi': {
      translation: "Moi (emphatique)",
      translationEn: "Me (emphatic)",
      explanation: "Forme longue du pronom 1re personne du singulier, utilisée pour insister sur le sujet.",
      explanationEn: "Long form of the first-person singular pronoun, used to emphasize the subject.",
      example: "Mimi ndiye niliyefanya. (C'est MOI qui l'ai fait — pas quelqu'un d'autre.)",
      exampleEn: "Mimi ndiye niliyefanya. (It's ME who did it — not someone else.)",
      audio: "Sw-mimi"
    },
    'Wewe': {
      translation: "Toi (emphatique)",
      translationEn: "You (emphatic, singular)",
      explanation: "Forme longue du pronom 2e personne du singulier.",
      explanationEn: "Long form of the second-person singular pronoun.",
      example: "Wewe ndiye uliyenisaidia. (C'est TOI qui m'as aidé(e).)",
      exampleEn: "Wewe ndiye uliyenisaidia. (It's YOU who helped me.)",
      audio: "Sw-wewe"
    },
    'Yeye': {
      translation: "Lui/Elle (emphatique)",
      translationEn: "Him/Her (emphatic)",
      explanation: "Forme longue du pronom 3e personne du singulier.",
      explanationEn: "Long form of the third-person singular pronoun.",
      example: "Yeye ndiye aliyekwenda. (C'est LUI/ELLE qui est parti(e).)",
      exampleEn: "Yeye ndiye aliyekwenda. (It's HIM/HER who left.)",
      audio: "Sw-yeye"
    },
    'Sisi': {
      translation: "Nous (emphatique)",
      translationEn: "We/Us (emphatic)",
      explanation: "Forme longue du pronom 1re personne du pluriel.",
      explanationEn: "Long form of the first-person plural pronoun.",
      example: "Sisi ndiyo tuliosema. (C'est NOUS qui avons parlé.)",
      exampleEn: "Sisi ndiyo tuliosema. (It's WE/US who spoke.)",
      audio: "Sw-sisi"
    },
    'Ninyi': {
      translation: "Vous (emphatique)",
      translationEn: "You (emphatic, plural)",
      explanation: "Forme longue du pronom 2e personne du pluriel.",
      explanationEn: "Long form of the second-person plural pronoun.",
      example: "Ninyi ndiyo mliosema. (C'est VOUS qui avez dit cela.)",
      exampleEn: "Ninyi ndiyo mliosema. (It's YOU who said that.)"
    },
    'Wao': {
      translation: "Eux/Elles (emphatique)",
      translationEn: "Them (emphatic)",
      explanation: "Forme longue du pronom 3e personne du pluriel.",
      explanationEn: "Long form of the third-person plural pronoun.",
      example: "Wao ndiyo waliokwenda. (Ce sont EUX/ELLES qui sont parti(e)s.)",
      exampleEn: "Wao ndiyo waliokwenda. (It's THEM who left.)",
      audio: "Sw-wao"
    },
  },

  'Pronoms objets': {
    'Ni (me)': {
      translation: "Me/Moi (objet)",
      translationEn: "Me (object)",
      explanation: "Infixe objet 1re personne du singulier, inséré entre le préfixe sujet et la racine verbale.",
      explanationEn: "First-person singular object infix, inserted between the subject prefix and the verb root.",
      example: "Ananiita. (Il/Elle m'appelle.)",
      exampleEn: "Ananiita. (He/She calls me.)"
    },
    'Ku': {
      translation: "Te/Toi (objet)",
      translationEn: "You (object, singular)",
      explanation: "Infixe objet 2e personne du singulier.",
      explanationEn: "Second-person singular object infix.",
      example: "Ninakuita. (Je t'appelle.)",
      exampleEn: "Ninakuita. (I call you.)"
    },
    'Mw/M': {
      translation: "Le/La/Lui (objet, personne)",
      translationEn: "Him/Her (object, person)",
      explanation: "Infixe objet 3e personne du singulier pour les personnes. 'Mw-' devant voyelle, 'M-' devant consonne.",
      explanationEn: "Third-person singular object infix for people. 'Mw-' before a vowel, 'M-' before a consonant.",
      example: "Ninamwita. (Je l'appelle.)",
      exampleEn: "Ninamwita. (I call him/her.)"
    },
    'Tu (nous)': {
      translation: "Nous (objet)",
      translationEn: "Us (object)",
      explanation: "Infixe objet 1re personne du pluriel.",
      explanationEn: "First-person plural object infix.",
      example: "Anatuita. (Il/Elle nous appelle.)",
      exampleEn: "Anatuita. (He/She calls us.)"
    },
    'Wa (vous)': {
      translation: "Vous (objet)",
      translationEn: "You (object, plural)",
      explanation: "Infixe objet 2e personne du pluriel.",
      explanationEn: "Second-person plural object infix.",
      example: "Ninawaita. (Je vous appelle.)",
      exampleEn: "Ninawaita. (I call you [plural].)"
    },
    'Wa (les)': {
      translation: "Les/Leur (objet)",
      translationEn: "Them (object)",
      explanation: "Infixe objet 3e personne du pluriel pour les personnes.",
      explanationEn: "Third-person plural object infix for people.",
      example: "Ninawaona. (Je les vois.)",
      exampleEn: "Ninawaona. (I see them.)"
    },
  },

  'Pronoms possessifs': {
    'Yangu': {
      translation: "Mon/Ma",
      translationEn: "My",
      explanation: "Possessif 1re personne du singulier — la forme varie selon la classe nominale du nom.",
      explanationEn: "First-person singular possessive — the form varies according to the noun's noun class.",
      example: "Nyumba yangu. (Ma maison.) — Kitabu changu. (Mon livre.)",
      exampleEn: "Nyumba yangu. (My house.) — Kitabu changu. (My book.)"
    },
    'Yako': {
      translation: "Ton/Ta",
      translationEn: "Your (singular)",
      explanation: "Possessif 2e personne du singulier.",
      explanationEn: "Second-person singular possessive.",
      example: "Nyumba yako. (Ta maison.) — Kitabu chako. (Ton livre.)",
      exampleEn: "Nyumba yako. (Your house.) — Kitabu chako. (Your book.)"
    },
    'Yake': {
      translation: "Son/Sa",
      translationEn: "His/Her",
      explanation: "Possessif 3e personne du singulier.",
      explanationEn: "Third-person singular possessive.",
      example: "Nyumba yake. (Sa maison.) — Kitabu chake. (Son livre.)",
      exampleEn: "Nyumba yake. (His/Her house.) — Kitabu chake. (His/Her book.)"
    },
    'Yetu': {
      translation: "Notre",
      translationEn: "Our",
      explanation: "Possessif 1re personne du pluriel.",
      explanationEn: "First-person plural possessive.",
      example: "Nyumba yetu. (Notre maison.) — Kitabu chetu. (Notre livre.)",
      exampleEn: "Nyumba yetu. (Our house.) — Kitabu chetu. (Our book.)"
    },
    'Yenu': {
      translation: "Votre",
      translationEn: "Your (plural)",
      explanation: "Possessif 2e personne du pluriel.",
      explanationEn: "Second-person plural possessive.",
      example: "Nyumba yenu. (Votre maison.) — Kitabu chenu. (Votre livre.)",
      exampleEn: "Nyumba yenu. (Your house.) — Kitabu chenu. (Your book.)"
    },
    'Yao': {
      translation: "Leur",
      translationEn: "Their",
      explanation: "Possessif 3e personne du pluriel.",
      explanationEn: "Third-person plural possessive.",
      example: "Nyumba yao. (Leur maison.) — Vitabu vyao. (Leurs livres.)",
      exampleEn: "Nyumba yao. (Their house.) — Vitabu vyao. (Their books.)"
    },
  },

  'Classes nominales': {
    'Classe M/Wa (personnes)': {
      translation: "Singulier/Pluriel — personnes",
      translationEn: "Singular/Plural — people",
      explanation: "Classe des êtres humains. Singulier en 'm-' ou 'mw-', pluriel en 'wa-'. La concordance nominale change selon cette classe.",
      explanationEn: "The class of human beings. Singular in 'm-' or 'mw-', plural in 'wa-'. Noun agreement changes according to this class.",
      example: "Mtu / Watu. (Une personne / Des personnes.) — Mwalimu / Walimu. (Un enseignant / Des enseignants.)",
      exampleEn: "Mtu / Watu. (A person / People.) — Mwalimu / Walimu. (A teacher / Teachers.)"
    },
    'Classe M/Mi (plantes, objets longs)': {
      translation: "Singulier/Pluriel — végétaux et objets longs",
      translationEn: "Singular/Plural — plants and long objects",
      explanation: "Classe des plantes, rivières et objets allongés. Singulier en 'm-', pluriel en 'mi-'.",
      explanationEn: "The class of plants, rivers, and elongated objects. Singular in 'm-', plural in 'mi-'.",
      example: "Mti / Miti. (Un arbre / Des arbres.) — Mto / Mito. (Une rivière / Des rivières.)",
      exampleEn: "Mti / Miti. (A tree / Trees.) — Mto / Mito. (A river / Rivers.)"
    },
    'Classe Ki/Vi (choses, langues)': {
      translation: "Singulier/Pluriel — choses et langues",
      translationEn: "Singular/Plural — things and languages",
      explanation: "Classe des objets, outils, langues. Singulier en 'ki-' ou 'ch-', pluriel en 'vi-' ou 'vy-'.",
      explanationEn: "The class of objects, tools, and languages. Singular in 'ki-' or 'ch-', plural in 'vi-' or 'vy-'.",
      example: "Kitu / Vitu. (Une chose / Des choses.) — Kiswahili. (La langue swahili.) — Chakula / Vyakula. (Un aliment / Des aliments.)",
      exampleEn: "Kitu / Vitu. (A thing / Things.) — Kiswahili. (The Swahili language.) — Chakula / Vyakula. (A food / Foods.)"
    },
    'Classe N/N (invariables, animaux)': {
      translation: "Singulier/Pluriel — invariables et animaux",
      translationEn: "Singular/Plural — invariable nouns and animals",
      explanation: "Classe dont la forme est identique au singulier et au pluriel. Inclut beaucoup d'animaux et de noms d'origine étrangère.",
      explanationEn: "The class whose form is identical in the singular and plural. Includes many animals and loanwords.",
      example: "Nyumba / Nyumba. (Une maison / Des maisons.) — Mbwa / Mbwa. (Un chien / Des chiens.)",
      exampleEn: "Nyumba / Nyumba. (A house / Houses.) — Mbwa / Mbwa. (A dog / Dogs.)"
    },
    'Classe Ji/Ma (fruits, augmentatifs)': {
      translation: "Singulier/Pluriel — fruits et augmentatifs",
      translationEn: "Singular/Plural — fruits and augmentatives",
      explanation: "Classe des fruits et de certains noms. Singulier sans préfixe ou en 'ji-', pluriel en 'ma-'.",
      explanationEn: "The class of fruits and certain nouns. Singular with no prefix or in 'ji-', plural in 'ma-'.",
      example: "Tunda / Matunda. (Un fruit / Des fruits.) — Jicho / Macho. (Un œil / Des yeux.) — Gari / Magari. (Une voiture / Des voitures.)",
      exampleEn: "Tunda / Matunda. (A fruit / Fruits.) — Jicho / Macho. (An eye / Eyes.) — Gari / Magari. (A car / Cars.)"
    },
    'Classe U/N (abstraits)': {
      translation: "Singulier — abstraits et concepts",
      translationEn: "Singular — abstract nouns and concepts",
      explanation: "Classe des noms abstraits et de certains noms. Souvent sans pluriel ou avec pluriel en classe N.",
      explanationEn: "The class of abstract nouns and certain other nouns. Often without a plural, or with a plural in the N class.",
      example: "Uhuru. (La liberté.) — Upendo. (L'amour.) — Uso / Nyuso. (Un visage / Des visages.)",
      exampleEn: "Uhuru. (Freedom.) — Upendo. (Love.) — Uso / Nyuso. (A face / Faces.)"
    },
  },

  'Structure de la phrase': {
    'Ordre SVO': {
      translation: "Sujet + Marqueur de temps + Verbe (+ Objet)",
      translationEn: "Subject + Tense marker + Verb (+ Object)",
      explanation: "Le swahili suit l'ordre SVO. Le préfixe sujet et le marqueur de temps s'agglutinent avant la racine verbale : Ni-na-soma = Je-PRÉSENT-lire.",
      explanationEn: "Swahili follows SVO word order. The subject prefix and tense marker agglutinate before the verb root: Ni-na-soma = I-PRESENT-read.",
      example: "Ninasoma kitabu. (Je lis un livre — Ni-na-soma kitabu.)",
      exampleEn: "Ninasoma kitabu. (I am reading a book — Ni-na-soma kitabu.)"
    },
    'Concordance nominale': {
      translation: "Accord adjectif-nom selon la classe",
      translationEn: "Adjective-noun agreement by class",
      explanation: "En swahili, l'adjectif s'accorde avec le nom selon sa classe nominale. Chaque classe a son préfixe d'accord.",
      explanationEn: "In Swahili, the adjective agrees with the noun according to its noun class. Each class has its own agreement prefix.",
      example: "Nyumba nzuri. (Une belle maison — classe N.) — Mtoto mzuri. (Un bel enfant — classe M/Wa.)",
      exampleEn: "Nyumba nzuri. (A beautiful house — N class.) — Mtoto mzuri. (A beautiful child — M/Wa class.)"
    },
    'Focus sujet (ndiye/ndiyo)': {
      translation: "C'est [sujet] qui...",
      translationEn: "It is [subject] who...",
      explanation: "'Ndiye' (pour les personnes) et 'ndiyo' (pour les choses) mettent en relief l'identité du sujet.",
      explanationEn: "'Ndiye' (for people) and 'ndiyo' (for things) highlight the identity of the subject.",
      example: "Yeye ndiye aliyekuja. (C'est LUI/ELLE qui est venu(e).)",
      exampleEn: "Yeye ndiye aliyekuja. (It's HIM/HER who came.)"
    },
    'Verbe être (ni)': {
      translation: "Être / C'est",
      translationEn: "To be / It is",
      explanation: "'Ni' est le verbe copule, équivalent de 'être' pour exprimer une identité ou une qualité permanente.",
      explanationEn: "'Ni' is the copula verb, equivalent to 'to be', used to express a permanent identity or quality.",
      example: "Mimi ni mwalimu. (Je suis enseignant.) — Yeye ni daktari. (Il/Elle est médecin.)",
      exampleEn: "Mimi ni mwalimu. (I am a teacher.) — Yeye ni daktari. (He/She is a doctor.)"
    },
    'Présentatif (yuko/niko/kiko)': {
      translation: "Être quelque part / Il y a",
      translationEn: "To be somewhere / There is",
      explanation: "Les formes 'niko/uko/yuko/tuko/mko/wako' expriment la localisation ou la présence d'une personne.",
      explanationEn: "The forms 'niko/uko/yuko/tuko/mko/wako' express the location or presence of a person.",
      example: "Yuko nyumbani. (Il/Elle est à la maison.) — Niko hapa. (Je suis ici.)",
      exampleEn: "Yuko nyumbani. (He/She is at home.) — Niko hapa. (I am here.)"
    },
    'Construction relative (-o-)': {
      translation: "Que, qui [relatif]",
      translationEn: "That, which, who [relative]",
      explanation: "L'infixe '-o-' insère une proposition relative. Il s'accorde avec la classe nominale du nom relativisé.",
      explanationEn: "The infix '-o-' inserts a relative clause. It agrees with the noun class of the noun being relativized.",
      example: "Kitabu nilichosoma. (Le livre que j'ai lu.) — Mtu niliyemwona. (La personne que j'ai vue.)",
      exampleEn: "Kitabu nilichosoma. (The book that I read.) — Mtu niliyemwona. (The person that I saw.)"
    },
  },

  // ============================================================
  // UNITÉ 2 — TEMPS VERBAUX, NÉGATION & QUESTIONS
  // ============================================================

  'Temps verbaux — Présent': {
    'Ninasoma': {
      translation: "Je lis (en cours / habituel)",
      translationEn: "I am reading (ongoing / habitual)",
      explanation: "Présent avec marqueur 'na-'. Exprime une action en cours ou habituelle à la 1re personne.",
      explanationEn: "Present tense with the marker 'na-'. Expresses an ongoing or habitual action in the first person.",
      example: "Ninasoma gazeti kila siku. (Je lis le journal chaque jour.)",
      exampleEn: "Ninasoma gazeti kila siku. (I read the newspaper every day.)"
    },
    'Unasoma': {
      translation: "Tu lis",
      translationEn: "You are reading",
      explanation: "Présent 2e personne du singulier.",
      explanationEn: "Present tense, second-person singular.",
      example: "Unasoma kitabu? (Tu lis un livre ?)",
      exampleEn: "Unasoma kitabu? (Are you reading a book?)"
    },
    'Anasoma': {
      translation: "Il/Elle lit",
      translationEn: "He/She is reading",
      explanation: "Présent 3e personne du singulier.",
      explanationEn: "Present tense, third-person singular.",
      example: "Anasoma barua. (Il/Elle lit une lettre.)",
      exampleEn: "Anasoma barua. (He/She is reading a letter.)"
    },
    'Tunasoma': {
      translation: "Nous lisons",
      translationEn: "We are reading",
      explanation: "Présent 1re personne du pluriel.",
      explanationEn: "Present tense, first-person plural.",
      example: "Tunasoma pamoja. (Nous lisons ensemble.)",
      exampleEn: "Tunasoma pamoja. (We are reading together.)"
    },
    'Mnasoma': {
      translation: "Vous lisez",
      translationEn: "You are reading (plural)",
      explanation: "Présent 2e personne du pluriel.",
      explanationEn: "Present tense, second-person plural.",
      example: "Mnasoma vizuri? (Vous lisez bien ?)",
      exampleEn: "Mnasoma vizuri? (Are you reading well?)"
    },
    'Husoma': {
      translation: "On lit (habituel neutre)",
      translationEn: "One reads (neutral habitual)",
      explanation: "Temps habituel 'hu-' — sans préfixe de personne spécifique. Exprime une vérité générale ou une habitude.",
      explanationEn: "Habitual tense 'hu-' — without a specific person prefix. Expresses a general truth or a habit.",
      example: "Husoma asubuhi. (On lit le matin. / Je lis habituellement le matin.)",
      exampleEn: "Husoma asubuhi. (One reads in the morning. / I usually read in the morning.)"
    },
  },

  'Temps verbaux — Passé': {
    'Nilisoma': {
      translation: "J'ai lu (passé simple)",
      translationEn: "I read (simple past)",
      explanation: "Passé simple avec marqueur 'li-'. Action accomplie dans le passé.",
      explanationEn: "Simple past with the marker 'li-'. An action completed in the past.",
      example: "Nilisoma kitabu jana. (J'ai lu un livre hier.)",
      exampleEn: "Nilisoma kitabu jana. (I read a book yesterday.)"
    },
    'Ulisoma': {
      translation: "Tu as lu",
      translationEn: "You read",
      explanation: "Passé simple 2e personne du singulier.",
      explanationEn: "Simple past, second-person singular.",
      example: "Ulisoma barua ile? (Tu as lu cette lettre ?)",
      exampleEn: "Ulisoma barua ile? (Did you read that letter?)"
    },
    'Alisoma': {
      translation: "Il/Elle a lu",
      translationEn: "He/She read",
      explanation: "Passé simple 3e personne du singulier.",
      explanationEn: "Simple past, third-person singular.",
      example: "Alisoma kitabu chote. (Il/Elle a lu tout le livre.)",
      exampleEn: "Alisoma kitabu chote. (He/She read the whole book.)"
    },
    'Nimesoma': {
      translation: "J'ai fini de lire (parfait)",
      translationEn: "I have finished reading (perfect)",
      explanation: "Parfait avec marqueur 'me-'. Exprime un état résultant d'une action passée, encore pertinent au présent.",
      explanationEn: "Perfect tense with the marker 'me-'. Expresses a state resulting from a past action that is still relevant in the present.",
      example: "Nimesoma tayari. (J'ai déjà lu — le résultat est présent.)",
      exampleEn: "Nimesoma tayari. (I have already read — the result is present.)"
    },
    'Amesoma': {
      translation: "Il/Elle a (déjà) lu",
      translationEn: "He/She has (already) read",
      explanation: "Parfait 3e personne du singulier.",
      explanationEn: "Perfect tense, third-person singular.",
      example: "Amesoma na anaelewa. (Il/Elle a lu et comprend.)",
      exampleEn: "Amesoma na anaelewa. (He/She has read and understands.)"
    },
    'Nilikuwa nikisoma': {
      translation: "J'étais en train de lire",
      translationEn: "I was reading",
      explanation: "Passé progressif (imparfait) — 'nilikuwa' (j'étais) + 'niki-' (marqueur de simultanéité). Action en cours dans le passé.",
      explanationEn: "Past progressive (imperfect) — 'nilikuwa' (I was) + 'niki-' (simultaneity marker). An action in progress in the past.",
      example: "Nilikuwa nikisoma alipoingia. (J'étais en train de lire quand il/elle est entré(e).)",
      exampleEn: "Nilikuwa nikisoma alipoingia. (I was reading when he/she came in.)"
    },
  },

  'Temps verbaux — Futur': {
    'Nitasoma': {
      translation: "Je lirai",
      translationEn: "I will read",
      explanation: "Futur simple avec marqueur 'ta-'. Action future certaine.",
      explanationEn: "Simple future with the marker 'ta-'. A certain future action.",
      example: "Nitasoma kesho. (Je lirai demain.)",
      exampleEn: "Nitasoma kesho. (I will read tomorrow.)"
    },
    'Utasoma': {
      translation: "Tu liras",
      translationEn: "You will read",
      explanation: "Futur simple 2e personne du singulier.",
      explanationEn: "Simple future, second-person singular.",
      example: "Utasoma kitabu hiki? (Tu liras ce livre ?)",
      exampleEn: "Utasoma kitabu hiki? (Will you read this book?)"
    },
    'Atasoma': {
      translation: "Il/Elle lira",
      translationEn: "He/She will read",
      explanation: "Futur simple 3e personne du singulier.",
      explanationEn: "Simple future, third-person singular.",
      example: "Atasoma vizuri. (Il/Elle lira bien.)",
      exampleEn: "Atasoma vizuri. (He/She will read well.)"
    },
    'Nikisoma': {
      translation: "Si je lis / Quand je lis [conditionnel]",
      translationEn: "If I read / When I read [conditional]",
      explanation: "Conditionnel avec marqueur 'ki-'. Exprime une condition ou une éventualité.",
      explanationEn: "Conditional with the marker 'ki-'. Expresses a condition or a possibility.",
      example: "Nikisoma, nitaelewa. (Si je lis, je comprendrai.)",
      exampleEn: "Nikisoma, nitaelewa. (If I read, I will understand.)"
    },
    'Ningesoma': {
      translation: "Je lirais [hypothétique]",
      translationEn: "I would read [hypothetical]",
      explanation: "Conditionnel hypothétique avec marqueur 'nge-'. Exprime ce qui se passerait dans des circonstances irréelles.",
      explanationEn: "Hypothetical conditional with the marker 'nge-'. Expresses what would happen under unreal circumstances.",
      example: "Ningesoma kama ningekuwa na wakati. (Je lirais si j'avais le temps.)",
      exampleEn: "Ningesoma kama ningekuwa na wakati. (I would read if I had time.)"
    },
    'Ningalisoma': {
      translation: "J'aurais lu [conditionnel passé]",
      translationEn: "I would have read [past conditional]",
      explanation: "Conditionnel passé avec marqueur 'ngali-'. Exprime ce qui aurait pu arriver mais n'est pas arrivé.",
      explanationEn: "Past conditional with the marker 'ngali-'. Expresses what could have happened but did not.",
      example: "Ningalisoma kama nisingesha kuchoka. (J'aurais lu si je n'avais pas été fatigué(e).)",
      exampleEn: "Ningalisoma kama nisingesha kuchoka. (I would have read if I had not been tired.)"
    },
  },

  'Temps verbaux — Impératif': {
    'Soma!': {
      translation: "Lis ! (singulier)",
      translationEn: "Read! (singular)",
      explanation: "Impératif 2e personne du singulier — forme de base de la racine verbale.",
      explanationEn: "Imperative, second-person singular — the base form of the verb root.",
      example: "Soma kitabu hiki! (Lis ce livre !)",
      exampleEn: "Soma kitabu hiki! (Read this book!)"
    },
    'Someni!': {
      translation: "Lisez ! (pluriel)",
      translationEn: "Read! (plural)",
      explanation: "Impératif 2e personne du pluriel. Le suffixe '-ni' marque l'impératif pluriel.",
      explanationEn: "Imperative, second-person plural. The suffix '-ni' marks the plural imperative.",
      example: "Someni kwa makini! (Lisez attentivement !)",
      exampleEn: "Someni kwa makini! (Read carefully!)"
    },
    'Nenda!': {
      translation: "Va ! (singulier)",
      translationEn: "Go! (singular)",
      explanation: "Impératif singulier du verbe kwenda (aller). Forme irrégulière.",
      explanationEn: "Singular imperative of the verb kwenda (to go). An irregular form.",
      example: "Nenda nyumbani! (Va à la maison !)",
      exampleEn: "Nenda nyumbani! (Go home!)"
    },
    'Nendeni!': {
      translation: "Allez ! (pluriel)",
      translationEn: "Go! (plural)",
      explanation: "Impératif pluriel du verbe kwenda.",
      explanationEn: "Plural imperative of the verb kwenda.",
      example: "Nendeni shuleni! (Allez à l'école !)",
      exampleEn: "Nendeni shuleni! (Go to school!)"
    },
    'Usisome!': {
      translation: "Ne lis pas ! (singulier négatif)",
      translationEn: "Don't read! (singular negative)",
      explanation: "Impératif négatif singulier avec 'usi-'. Interdit une action à une seule personne.",
      explanationEn: "Negative singular imperative with 'usi-'. Forbids an action to a single person.",
      example: "Usisome wakati wa chakula! (Ne lis pas pendant le repas !)",
      exampleEn: "Usisome wakati wa chakula! (Don't read during the meal!)"
    },
    'Msisome!': {
      translation: "Ne lisez pas ! (pluriel négatif)",
      translationEn: "Don't read! (plural negative)",
      explanation: "Impératif négatif pluriel avec 'msi-'. Interdit une action à plusieurs personnes.",
      explanationEn: "Negative plural imperative with 'msi-'. Forbids an action to several people.",
      example: "Msisome wakati nasema! (Ne lisez pas quand je parle !)",
      exampleEn: "Msisome wakati nasema! (Don't read while I'm speaking!)"
    },
  },

  'Négation': {
    'Sisomi': {
      translation: "Je ne lis pas",
      translationEn: "I don't read",
      explanation: "Négation du présent 1re personne. Le préfixe sujet négatif remplace 'Ni' par 'Si'.",
      explanationEn: "Negation of the present tense, first person. The negative subject prefix replaces 'Ni' with 'Si'.",
      example: "Sisomi vitabu vya watoto. (Je ne lis pas les livres pour enfants.)",
      exampleEn: "Sisomi vitabu vya watoto. (I don't read children's books.)"
    },
    'Husomi': {
      translation: "Tu ne lis pas",
      translationEn: "You don't read",
      explanation: "Négation du présent 2e personne du singulier. 'U' devient 'Hu'.",
      explanationEn: "Negation of the present tense, second-person singular. 'U' becomes 'Hu'.",
      example: "Husomi sana. (Tu ne lis pas beaucoup.)",
      exampleEn: "Husomi sana. (You don't read much.)"
    },
    'Hasomi': {
      translation: "Il/Elle ne lit pas",
      translationEn: "He/She doesn't read",
      explanation: "Négation du présent 3e personne du singulier. 'A' devient 'Ha'.",
      explanationEn: "Negation of the present tense, third-person singular. 'A' becomes 'Ha'.",
      example: "Hasomi gazeti. (Il/Elle ne lit pas le journal.)",
      exampleEn: "Hasomi gazeti. (He/She doesn't read the newspaper.)"
    },
    'Sikuenda': {
      translation: "Je ne suis pas allé(e)",
      translationEn: "I didn't go",
      explanation: "Négation du passé simple 1re personne. 'Nili-' devient 'Siku-'.",
      explanationEn: "Negation of the simple past, first person. 'Nili-' becomes 'Siku-'.",
      example: "Sikuenda shuleni jana. (Je ne suis pas allé(e) à l'école hier.)",
      exampleEn: "Sikuenda shuleni jana. (I didn't go to school yesterday.)"
    },
    'Sitasoma': {
      translation: "Je ne lirai pas",
      translationEn: "I will not read",
      explanation: "Négation du futur 1re personne. Le préfixe 'Si-' remplace 'Ni-' et le marqueur 'ta-' reste.",
      explanationEn: "Negation of the future, first person. The prefix 'Si-' replaces 'Ni-' and the marker 'ta-' remains.",
      example: "Sitasoma kesho kwa sababu nitafanya kazi. (Je ne lirai pas demain car je travaillerai.)",
      exampleEn: "Sitasoma kesho kwa sababu nitafanya kazi. (I will not read tomorrow because I will be working.)"
    },
    'Sijasoma': {
      translation: "Je n'ai pas encore lu",
      translationEn: "I haven't read yet",
      explanation: "Négation du parfait 1re personne. 'Nime-' devient 'Sija-'. Exprime l'absence d'accomplissement d'une action attendue.",
      explanationEn: "Negation of the perfect tense, first person. 'Nime-' becomes 'Sija-'. Expresses the non-completion of an expected action.",
      example: "Sijasoma barua yako bado. (Je n'ai pas encore lu ta lettre.)",
      exampleEn: "Sijasoma barua yako bado. (I haven't read your letter yet.)"
    },
  },

  'Questions': {
    'Nani?': {
      translation: "Qui ?",
      translationEn: "Who?",
      explanation: "'Nani' interroge sur une personne. Il peut être sujet ou objet de la phrase.",
      explanationEn: "'Nani' asks about a person. It can be the subject or object of the sentence.",
      example: "Nani alifanya hivi? (Qui a fait cela ?)",
      exampleEn: "Nani alifanya hivi? (Who did this?)"
    },
    'Nini?': {
      translation: "Quoi ? / Qu'est-ce que ?",
      translationEn: "What?",
      explanation: "'Nini' interroge sur une chose ou une action.",
      explanationEn: "'Nini' asks about a thing or an action.",
      example: "Unataka nini? (Qu'est-ce que tu veux ?)",
      exampleEn: "Unataka nini? (What do you want?)"
    },
    'Wapi?': {
      translation: "Où ?",
      translationEn: "Where?",
      explanation: "'Wapi' interroge sur un lieu ou une direction.",
      explanationEn: "'Wapi' asks about a place or a direction.",
      example: "Unakwenda wapi? (Où vas-tu ?)",
      exampleEn: "Unakwenda wapi? (Where are you going?)"
    },
    'Lini?': {
      translation: "Quand ?",
      translationEn: "When?",
      explanation: "'Lini' interroge sur le moment d'une action.",
      explanationEn: "'Lini' asks about the time of an action.",
      example: "Unafika lini? (Quand est-ce que tu arrives ?)",
      exampleEn: "Unafika lini? (When are you arriving?)"
    },
    'Vipi?': {
      translation: "Comment ?",
      translationEn: "How?",
      explanation: "'Vipi' interroge sur la manière. Aussi utilisé dans les salutations : 'Habari yako?' / 'Vipi?' = Comment ça va ?",
      explanationEn: "'Vipi' asks about the manner. Also used in greetings: 'Habari yako?' / 'Vipi?' = How's it going?",
      example: "Unafanya vipi? (Comment fais-tu ?)",
      exampleEn: "Unafanya vipi? (How do you do it?)"
    },
    'Kwa nini?': {
      translation: "Pourquoi ?",
      translationEn: "Why?",
      explanation: "'Kwa nini' interroge sur la cause ou le motif d'une action.",
      explanationEn: "'Kwa nini' asks about the cause or reason for an action.",
      example: "Kwa nini hukuja? (Pourquoi tu n'es pas venu(e) ?)",
      exampleEn: "Kwa nini hukuja? (Why didn't you come?)",
      audio: "Sw-kwa_nini"
    },
    'Ngapi?': {
      translation: "Combien ?",
      translationEn: "How many? / How much?",
      explanation: "'Ngapi' interroge sur une quantité ou un nombre.",
      explanationEn: "'Ngapi' asks about a quantity or a number.",
      example: "Inagharimu ngapi? (Combien ça coûte ?)",
      exampleEn: "Inagharimu ngapi? (How much does it cost?)"
    },
    'Je...?': {
      translation: "Est-ce que... ?",
      translationEn: "Is it that...? [yes/no question marker]",
      explanation: "'Je' en tête de phrase transforme une affirmation en question fermée (oui/non). Peut aussi exprimer la surprise.",
      explanationEn: "'Je' at the start of a sentence turns a statement into a yes/no question. It can also express surprise.",
      example: "Je, unaenda? (Est-ce que tu y vas ?)",
      exampleEn: "Je, unaenda? (Are you going?)"
    },
  },

  // ============================================================
  // UNITÉ 3 — PRÉPOSITIONS, ADJECTIFS, ADVERBES & CONJONCTIONS
  // ============================================================

  'Prépositions de lieu': {
    'Katika': {
      translation: "Dans, à, en",
      translationEn: "In, at, inside",
      explanation: "Préposition de lieu générale, indique la position à l'intérieur ou dans un espace.",
      explanationEn: "General locative preposition, indicates a position inside or within a space.",
      example: "Kiko katika nyumba. (C'est dans la maison.)",
      exampleEn: "Kiko katika nyumba. (It is in the house.)"
    },
    'Juu ya': {
      translation: "Sur, au-dessus de",
      translationEn: "On, above",
      explanation: "Locution prépositionnelle indiquant une position supérieure.",
      explanationEn: "Prepositional phrase indicating a position above.",
      example: "Kitabu kiko juu ya meza. (Le livre est sur la table.)",
      exampleEn: "Kitabu kiko juu ya meza. (The book is on the table.)"
    },
    'Chini ya': {
      translation: "Sous, en dessous de",
      translationEn: "Under, beneath",
      explanation: "Locution prépositionnelle indiquant une position inférieure.",
      explanationEn: "Prepositional phrase indicating a position below.",
      example: "Paka yu chini ya meza. (Le chat est sous la table.)",
      exampleEn: "Paka yu chini ya meza. (The cat is under the table.)"
    },
    'Karibu na': {
      translation: "Près de, à côté de",
      translationEn: "Near, next to",
      explanation: "Locution prépositionnelle indiquant la proximité.",
      explanationEn: "Prepositional phrase indicating proximity.",
      example: "Nyumba yangu iko karibu na shule. (Ma maison est près de l'école.)",
      exampleEn: "Nyumba yangu iko karibu na shule. (My house is near the school.)"
    },
    'Mbali na': {
      translation: "Loin de",
      translationEn: "Far from",
      explanation: "Locution prépositionnelle indiquant l'éloignement.",
      explanationEn: "Prepositional phrase indicating distance.",
      example: "Hospitali iko mbali na hapa. (L'hôpital est loin d'ici.)",
      exampleEn: "Hospitali iko mbali na hapa. (The hospital is far from here.)"
    },
    'Mbele ya': {
      translation: "Devant, en face de",
      translationEn: "In front of, facing",
      explanation: "Locution prépositionnelle indiquant une position antérieure ou frontale.",
      explanationEn: "Prepositional phrase indicating a frontal or forward position.",
      example: "Simama mbele ya darasa. (Tiens-toi devant la classe.)",
      exampleEn: "Simama mbele ya darasa. (Stand in front of the class.)"
    },
    'Nyuma ya': {
      translation: "Derrière",
      translationEn: "Behind",
      explanation: "Locution prépositionnelle indiquant une position postérieure.",
      explanationEn: "Prepositional phrase indicating a position behind.",
      example: "Paka yuko nyuma ya mlango. (Le chat est derrière la porte.)",
      exampleEn: "Paka yuko nyuma ya mlango. (The cat is behind the door.)"
    },
    'Kati ya': {
      translation: "Entre, au milieu de",
      translationEn: "Between, in the middle of",
      explanation: "Locution prépositionnelle indiquant une position intermédiaire.",
      explanationEn: "Prepositional phrase indicating an intermediate position.",
      example: "Kaa kati ya Amina na Juma. (Assieds-toi entre Amina et Juma.)",
      exampleEn: "Kaa kati ya Amina na Juma. (Sit between Amina and Juma.)"
    },
    'Nje ya': {
      translation: "En dehors de, à l'extérieur de",
      translationEn: "Outside of",
      explanation: "Locution prépositionnelle indiquant l'extériorité.",
      explanationEn: "Prepositional phrase indicating exteriority.",
      example: "Watoto wanacheza nje ya nyumba. (Les enfants jouent à l'extérieur de la maison.)",
      exampleEn: "Watoto wanacheza nje ya nyumba. (The children are playing outside the house.)"
    },
    'Ndani ya': {
      translation: "À l'intérieur de",
      translationEn: "Inside",
      explanation: "Locution prépositionnelle indiquant l'intériorité avec précision.",
      explanationEn: "Prepositional phrase indicating interiority precisely.",
      example: "Paka yuko ndani ya sanduku. (Le chat est à l'intérieur de la boîte.)",
      exampleEn: "Paka yuko ndani ya sanduku. (The cat is inside the box.)"
    },
    'Hapa': {
      translation: "Ici",
      translationEn: "Here",
      explanation: "Adverbe de lieu indiquant la proximité immédiate du locuteur.",
      explanationEn: "Locative adverb indicating the immediate proximity of the speaker.",
      example: "Kaa hapa. (Reste ici.)",
      exampleEn: "Kaa hapa. (Stay here.)"
    },
    'Pale': {
      translation: "Là, là-bas",
      translationEn: "There, over there",
      explanation: "Adverbe de lieu indiquant un endroit éloigné du locuteur.",
      explanationEn: "Locative adverb indicating a place away from the speaker.",
      example: "Nenda pale. (Va là-bas.)",
      exampleEn: "Nenda pale. (Go over there.)"
    },
  },

  'Prépositions de temps et de relation': {
    'Baada ya': {
      translation: "Après",
      translationEn: "After",
      explanation: "Locution prépositionnelle temporelle de postériorité.",
      explanationEn: "Temporal prepositional phrase indicating what comes after.",
      example: "Baada ya kula, nitasoma. (Après avoir mangé, je lirai.)",
      exampleEn: "Baada ya kula, nitasoma. (After eating, I will read.)"
    },
    'Kabla ya': {
      translation: "Avant",
      translationEn: "Before",
      explanation: "Locution prépositionnelle temporelle d'antériorité.",
      explanationEn: "Temporal prepositional phrase indicating what comes before.",
      example: "Kabla ya kwenda, niambie. (Avant d'aller, dis-le-moi.)",
      exampleEn: "Kabla ya kwenda, niambie. (Before going, tell me.)"
    },
    'Wakati wa': {
      translation: "Pendant, au moment de",
      translationEn: "During, at the time of",
      explanation: "Indique une durée ou un moment coïncidant avec une action.",
      explanationEn: "Indicates a duration or a moment coinciding with an action.",
      example: "Wakati wa masomo, nyamaza. (Pendant les cours, sois silencieux.)",
      exampleEn: "Wakati wa masomo, nyamaza. (During class, be quiet.)"
    },
    'Tangu': {
      translation: "Depuis",
      translationEn: "Since",
      explanation: "Indique le point de départ dans le temps.",
      explanationEn: "Indicates the starting point in time.",
      example: "Tangu asubuhi, nimekuwa nikifanya kazi. (Depuis ce matin, je travaille.)",
      exampleEn: "Tangu asubuhi, nimekuwa nikifanya kazi. (Since this morning, I have been working.)",
      audio: "Sw-tangu"
    },
    'Hadi': {
      translation: "Jusqu'à",
      translationEn: "Until",
      explanation: "Indique une limite dans le temps ou l'espace.",
      explanationEn: "Indicates a limit in time or space.",
      example: "Subiri hadi kesho. (Attends jusqu'à demain.)",
      exampleEn: "Subiri hadi kesho. (Wait until tomorrow.)"
    },
    'Na': {
      translation: "Avec, et",
      translationEn: "With, and",
      explanation: "Conjonction et préposition d'accompagnement ou d'association.",
      explanationEn: "Conjunction and preposition of accompaniment or association.",
      example: "Nenda na mimi. (Viens avec moi.)",
      exampleEn: "Nenda na mimi. (Come with me.)"
    },
    'Bila': {
      translation: "Sans",
      translationEn: "Without",
      explanation: "Préposition d'exclusion ou d'absence.",
      explanationEn: "Preposition of exclusion or absence.",
      example: "Alifika bila chakula. (Il/Elle est arrivé(e) sans nourriture.)",
      exampleEn: "Alifika bila chakula. (He/She arrived without food.)"
    },
    'Kwa': {
      translation: "Chez, par, avec, pour",
      translationEn: "At, by, with, for",
      explanation: "Préposition polyvalente indiquant le moyen, la cause, la direction ou le destinataire.",
      explanationEn: "Versatile preposition indicating means, cause, direction, or recipient.",
      example: "Nenda kwa daktari. (Va chez le médecin.)",
      exampleEn: "Nenda kwa daktari. (Go to the doctor.)"
    },
    'Kama': {
      translation: "Comme, tel que",
      translationEn: "Like, such as",
      explanation: "Indique une comparaison ou une ressemblance.",
      explanationEn: "Indicates a comparison or a resemblance.",
      example: "Anaimba kama ndege. (Il/Elle chante comme un oiseau.)",
      exampleEn: "Anaimba kama ndege. (He/She sings like a bird.)"
    },
  },

  'Adjectifs — Apparence physique': {
    'Mzuri / Nzuri': {
      translation: "Beau, belle, bon(ne)",
      translationEn: "Beautiful, good, nice",
      explanation: "Adjectif qualificatif positif général. La forme varie selon la classe nominale (Mzuri pour M/Wa, Nzuri pour N, etc.).",
      explanationEn: "General positive qualifying adjective. The form varies according to the noun class (Mzuri for M/Wa, Nzuri for N, etc.).",
      example: "Nyumba nzuri. (Une belle maison.) — Mtoto mzuri. (Un bel enfant.)",
      exampleEn: "Nyumba nzuri. (A beautiful house.) — Mtoto mzuri. (A beautiful child.)",
      audio: "Sw-mzuri"
    },
    'Mbaya': {
      translation: "Mauvais, laid",
      translationEn: "Bad, ugly",
      explanation: "Adjectif qualificatif négatif, opposé de mzuri.",
      explanationEn: "Negative qualifying adjective, the opposite of mzuri.",
      example: "Hali ya anga mbaya. (Le temps est mauvais.)",
      exampleEn: "Hali ya anga mbaya. (The weather is bad.)"
    },
    'Mkubwa / Kubwa': {
      translation: "Grand, gros",
      translationEn: "Big, large",
      explanation: "Adjectif de taille indiquant une grande dimension. 'Mkubwa' pour M/Wa, 'Kubwa' pour les autres.",
      explanationEn: "Size adjective indicating a large dimension. 'Mkubwa' for M/Wa, 'Kubwa' for other classes.",
      example: "Nyumba kubwa. (Une grande maison.) — Mtu mkubwa. (Un grand homme.)",
      exampleEn: "Nyumba kubwa. (A big house.) — Mtu mkubwa. (A big man.)"
    },
    'Mdogo / Ndogo': {
      translation: "Petit, jeune",
      translationEn: "Small, young",
      explanation: "Adjectif de taille indiquant une petite dimension ou la jeunesse.",
      explanationEn: "Size adjective indicating a small dimension or youth.",
      example: "Mtoto mdogo. (Un petit enfant.) — Nyumba ndogo. (Une petite maison.)",
      exampleEn: "Mtoto mdogo. (A small child.) — Nyumba ndogo. (A small house.)"
    },
    'Mrefu / Refu': {
      translation: "Long, grand (stature)",
      translationEn: "Long, tall (stature)",
      explanation: "Adjectif de longueur ou de hauteur.",
      explanationEn: "Adjective of length or height.",
      example: "Mti mrefu. (Un grand arbre.) — Mkono mrefu. (Un bras long.)",
      exampleEn: "Mti mrefu. (A tall tree.) — Mkono mrefu. (A long arm.)"
    },
    'Mfupi / Fupi': {
      translation: "Court, petit (stature)",
      translationEn: "Short (length or stature)",
      explanation: "Adjectif de longueur courte, opposé de mrefu.",
      explanationEn: "Adjective of short length, the opposite of mrefu.",
      example: "Kamba fupi. (Une corde courte.) — Mtu mfupi. (Un homme de petite taille.)",
      exampleEn: "Kamba fupi. (A short rope.) — Mtu mfupi. (A short man.)"
    },
    'Mnene / Nene': {
      translation: "Gros, épais, corpulent",
      translationEn: "Fat, thick, stout",
      explanation: "Décrit une forte corpulence ou une grande épaisseur.",
      explanationEn: "Describes a heavy build or great thickness.",
      example: "Mtu mnene. (Une personne corpulente.) — Ukuta nene. (Un mur épais.)",
      exampleEn: "Mtu mnene. (A stout person.) — Ukuta nene. (A thick wall.)"
    },
    'Mwembamba / Wembamba': {
      translation: "Mince, svelte",
      translationEn: "Thin, slender",
      explanation: "Décrit une faible corpulence ou une finesse, opposé de mnene.",
      explanationEn: "Describes a slight build or slenderness, the opposite of mnene.",
      example: "Msichana mwembamba. (Une jeune fille svelte.) — Fimbo nyembamba. (Un bâton mince.)",
      exampleEn: "Msichana mwembamba. (A slender girl.) — Fimbo nyembamba. (A thin stick.)"
    },
  },

  'Adjectifs — Caractère': {
    'Mwema / Wema': {
      translation: "Bon, gentil, de bon cœur",
      translationEn: "Good, kind, good-hearted",
      explanation: "Décrit une personne de bonne nature et aux bonnes intentions.",
      explanationEn: "Describes a person of good nature and good intentions.",
      example: "Mtu mwema. (Une personne de bon cœur.)",
      exampleEn: "Mtu mwema. (A good-hearted person.)"
    },
    'Mkarimu / Karimu': {
      translation: "Généreux, charitable",
      translationEn: "Generous, charitable",
      explanation: "Décrit une personne qui aime donner et partager avec les autres.",
      explanationEn: "Describes a person who likes to give and share with others.",
      example: "Mwenyeji mkarimu. (Un hôte généreux.)",
      exampleEn: "Mwenyeji mkarimu. (A generous host.)"
    },
    'Mwerevu / Werevu': {
      translation: "Intelligent, malin, astucieux",
      translationEn: "Intelligent, clever, cunning",
      explanation: "Décrit une personne perspicace et vive d'esprit.",
      explanationEn: "Describes a perceptive, sharp-witted person.",
      example: "Mtoto mwerevu. (Un enfant intelligent.)",
      exampleEn: "Mtoto mwerevu. (An intelligent child.)"
    },
    'Mvumilivu / Vumilivu': {
      translation: "Patient, endurant",
      translationEn: "Patient, enduring",
      explanation: "Décrit une personne qui sait attendre et supporter les difficultés.",
      explanationEn: "Describes a person who knows how to wait and endure difficulties.",
      example: "Mwalimu mvumilivu. (Un enseignant patient.)",
      exampleEn: "Mwalimu mvumilivu. (A patient teacher.)"
    },
    'Jasiri': {
      translation: "Courageux, brave",
      translationEn: "Courageous, brave",
      explanation: "Décrit une personne qui n'a pas peur et affronte les dangers.",
      explanationEn: "Describes a person who is not afraid and faces dangers.",
      example: "Askari jasiri. (Un soldat courageux.)",
      exampleEn: "Askari jasiri. (A brave soldier.)"
    },
    'Mwaminifu / Waaminifu': {
      translation: "Honnête, fidèle, loyal",
      translationEn: "Honest, faithful, loyal",
      explanation: "Décrit une personne digne de confiance et sincère.",
      explanationEn: "Describes a trustworthy and sincere person.",
      example: "Rafiki mwaminifu. (Un ami fidèle.)",
      exampleEn: "Rafiki mwaminifu. (A loyal friend.)"
    },
    'Mpole / Wapole': {
      translation: "Poli, doux, calme",
      translationEn: "Polite, gentle, calm",
      explanation: "Décrit une personne aux manières douces et respectueuses.",
      explanationEn: "Describes a person with gentle, respectful manners.",
      example: "Mtoto mpole. (Un enfant poli et calme.)",
      exampleEn: "Mtoto mpole. (A polite, calm child.)"
    },
    'Mvivu / Wavivu': {
      translation: "Paresseux",
      translationEn: "Lazy",
      explanation: "Décrit une personne qui évite de travailler ou de faire des efforts.",
      explanationEn: "Describes a person who avoids working or making an effort.",
      example: "Mwanafunzi mvivu. (Un élève paresseux.)",
      exampleEn: "Mwanafunzi mvivu. (A lazy student.)"
    },
  },

  'Adjectifs — États et sensations': {
    'Moto / Joto': {
      translation: "Chaud",
      translationEn: "Hot",
      explanation: "'Moto' qualifie un objet chaud, 'joto' désigne la chaleur comme état ou sensation.",
      explanationEn: "'Moto' describes a hot object, while 'joto' refers to heat as a state or sensation.",
      example: "Maji moto. (De l'eau chaude.) — Kuna joto leo. (Il fait chaud aujourd'hui.)",
      exampleEn: "Maji moto. (Hot water.) — Kuna joto leo. (It's hot today.)"
    },
    'Baridi': {
      translation: "Froid, frais",
      translationEn: "Cold, cool",
      explanation: "Décrit la sensation ou la température de fraîcheur ou de froid.",
      explanationEn: "Describes the sensation or temperature of coolness or cold.",
      example: "Maji baridi. (De l'eau fraîche.) — Hewa baridi. (L'air est frais.)",
      exampleEn: "Maji baridi. (Cold water.) — Hewa baridi. (The air is cool.)"
    },
    'Tamu': {
      translation: "Délicieux, doux, agréable",
      translationEn: "Delicious, sweet, pleasant",
      explanation: "Décrit quelque chose d'agréable au goût ou à l'expérience.",
      explanationEn: "Describes something pleasant to taste or experience.",
      example: "Chakula hiki ni tamu sana. (Cette nourriture est très délicieuse.)",
      exampleEn: "Chakula hiki ni tamu sana. (This food is very delicious.)"
    },
    'Chungu': {
      translation: "Amer, désagréable",
      translationEn: "Bitter, unpleasant",
      explanation: "Décrit un goût amer ou quelque chose de difficile, opposé de tamu.",
      explanationEn: "Describes a bitter taste or something unpleasant, the opposite of tamu.",
      example: "Dawa hii ni chungu. (Ce médicament est amer.)",
      exampleEn: "Dawa hii ni chungu. (This medicine is bitter.)"
    },
    'Safi': {
      translation: "Propre, pur",
      translationEn: "Clean, pure",
      explanation: "Décrit quelque chose de propre, net et sans souillure.",
      explanationEn: "Describes something clean, neat, and unsoiled.",
      example: "Nguo safi. (Des vêtements propres.) — Maji safi. (De l'eau pure.)",
      exampleEn: "Nguo safi. (Clean clothes.) — Maji safi. (Pure water.)"
    },
    'Chafu': {
      translation: "Sale, souillé",
      translationEn: "Dirty, soiled",
      explanation: "Décrit quelque chose de sale, opposé de safi.",
      explanationEn: "Describes something dirty, the opposite of safi.",
      example: "Mikono chafu. (Des mains sales.)",
      exampleEn: "Mikono chafu. (Dirty hands.)"
    },
    'Mchoka / Wachoka': {
      translation: "Fatigué, épuisé",
      translationEn: "Tired, exhausted",
      explanation: "Décrit un état de fatigue physique ou morale.",
      explanationEn: "Describes a state of physical or mental fatigue.",
      example: "Mimi ni mchoka sana leo. (Je suis très fatigué(e) aujourd'hui.)",
      exampleEn: "Mimi ni mchoka sana leo. (I am very tired today.)"
    },
    'Njaa': {
      translation: "Affamé / La faim",
      translationEn: "Hungry / Hunger",
      explanation: "Décrit l'état d'avoir faim. 'Nina njaa' = J'ai faim.",
      explanationEn: "Describes the state of being hungry. 'Nina njaa' = I am hungry.",
      example: "Nina njaa. (J'ai faim.) — Mtoto ana njaa. (L'enfant a faim.)",
      exampleEn: "Nina njaa. (I am hungry.) — Mtoto ana njaa. (The child is hungry.)"
    },
  },

  'Adjectifs — Quantité et jugement': {
    'Nyingi': {
      translation: "Nombreux, beaucoup de",
      translationEn: "Many, a lot of",
      explanation: "Décrit une grande quantité. La forme varie selon la classe nominale.",
      explanationEn: "Describes a large quantity. The form varies according to the noun class.",
      example: "Watu wengi. (Beaucoup de gens.) — Vitabu vingi. (Beaucoup de livres.)",
      exampleEn: "Watu wengi. (Many people.) — Vitabu vingi. (Many books.)"
    },
    'Chache': {
      translation: "Peu, rare",
      translationEn: "Few, scarce",
      explanation: "Décrit une faible quantité.",
      explanationEn: "Describes a small quantity.",
      example: "Watu wachache. (Peu de gens.) — Pesa chache. (Peu d'argent.)",
      exampleEn: "Watu wachache. (Few people.) — Pesa chache. (Little money.)"
    },
    'Rahisi': {
      translation: "Facile, simple",
      translationEn: "Easy, simple",
      explanation: "Décrit quelque chose d'aisé à réaliser ou de bon marché.",
      explanationEn: "Describes something easy to do or inexpensive.",
      example: "Kazi hii ni rahisi. (Ce travail est facile.) — Bei rahisi. (Prix abordable.)",
      exampleEn: "Kazi hii ni rahisi. (This work is easy.) — Bei rahisi. (An affordable price.)"
    },
    'Ngumu': {
      translation: "Difficile, dur, solide",
      translationEn: "Difficult, hard, solid",
      explanation: "Décrit quelque chose de difficile ou de physiquement dur.",
      explanationEn: "Describes something difficult or physically hard.",
      example: "Mtihani ngumu. (Un examen difficile.) — Jiwe gumu. (Une pierre dure.)",
      exampleEn: "Mtihani ngumu. (A difficult exam.) — Jiwe gumu. (A hard stone.)"
    },
    'Mpya / Mipya': {
      translation: "Nouveau, neuf",
      translationEn: "New",
      explanation: "Décrit quelque chose de récent ou qui n'a pas encore servi.",
      explanationEn: "Describes something recent or unused.",
      example: "Gari jipya. (Une voiture neuve.) — Nyumba mpya. (Une nouvelle maison.)",
      exampleEn: "Gari jipya. (A new car.) — Nyumba mpya. (A new house.)"
    },
    'Mzee / Wazee': {
      translation: "Vieux, âgé",
      translationEn: "Old, elderly",
      explanation: "Décrit un âge avancé ou quelque chose d'ancien.",
      explanationEn: "Describes an advanced age or something old.",
      example: "Mzee mkongwe. (Un vieil homme.) — Nyumba ya zamani. (Une vieille maison.)",
      exampleEn: "Mzee mkongwe. (An elderly man.) — Nyumba ya zamani. (An old house.)"
    },
    'Kamili': {
      translation: "Complet, entier, parfait",
      translationEn: "Complete, whole, perfect",
      explanation: "Décrit quelque chose de complet, sans manque ni défaut.",
      explanationEn: "Describes something complete, without any deficiency or flaw.",
      example: "Kazi kamili. (Un travail complet/parfait.)",
      exampleEn: "Kazi kamili. (A complete/perfect piece of work.)"
    },
  },

  'Conjonctions': {
    'Na': {
      translation: "Et, avec",
      translationEn: "And, with",
      explanation: "'Na' relie deux noms, groupes nominaux ou propositions. C'est la conjonction de coordination la plus fréquente.",
      explanationEn: "'Na' links two nouns, noun phrases, or clauses. It is the most common coordinating conjunction.",
      example: "Amina na Juma wanakwenda. (Amina et Juma y vont.)",
      exampleEn: "Amina na Juma wanakwenda. (Amina and Juma are going.)"
    },
    'Lakini': {
      translation: "Mais",
      translationEn: "But",
      explanation: "'Lakini' introduit une opposition ou une restriction entre deux propositions.",
      explanationEn: "'Lakini' introduces a contrast or restriction between two clauses.",
      example: "Nataka kwenda, lakini sina wakati. (Je veux y aller, mais je n'ai pas le temps.)",
      exampleEn: "Nataka kwenda, lakini sina wakati. (I want to go, but I don't have time.)"
    },
    'Au': {
      translation: "Ou",
      translationEn: "Or",
      explanation: "'Au' introduit une alternative entre deux options.",
      explanationEn: "'Au' introduces an alternative between two options.",
      example: "Unataka chai au kahawa? (Tu veux du thé ou du café ?)",
      exampleEn: "Unataka chai au kahawa? (Do you want tea or coffee?)"
    },
    'Kwa sababu': {
      translation: "Parce que, car",
      translationEn: "Because",
      explanation: "'Kwa sababu' introduit une proposition causale expliquant la raison d'une action.",
      explanationEn: "'Kwa sababu' introduces a causal clause explaining the reason for an action.",
      example: "Sikuja kwa sababu nilikuwa mgonjwa. (Je ne suis pas venu(e) parce que j'étais malade.)",
      exampleEn: "Sikuja kwa sababu nilikuwa mgonjwa. (I didn't come because I was sick.)"
    },
    'Kama': {
      translation: "Si, comme",
      translationEn: "If, as",
      explanation: "'Kama' introduit une condition hypothétique ou une comparaison.",
      explanationEn: "'Kama' introduces a hypothetical condition or a comparison.",
      example: "Kama una wakati, njoo. (Si tu as le temps, viens.)",
      exampleEn: "Kama una wakati, njoo. (If you have time, come.)"
    },
    'Kwa hivyo': {
      translation: "Donc, alors, c'est pourquoi",
      translationEn: "So, therefore, that's why",
      explanation: "'Kwa hivyo' marque la conséquence logique d'un état ou d'une action.",
      explanationEn: "'Kwa hivyo' marks the logical consequence of a state or an action.",
      example: "Nilikuwa mgonjwa, kwa hivyo sikuja. (J'étais malade, donc je ne suis pas venu(e).)",
      exampleEn: "Nilikuwa mgonjwa, kwa hivyo sikuja. (I was sick, so I didn't come.)"
    },
    'Ingawa': {
      translation: "Bien que, quoique",
      translationEn: "Although, even though",
      explanation: "'Ingawa' introduit une concession — une proposition qui s'oppose à l'attendu.",
      explanationEn: "'Ingawa' introduces a concession — a clause that contrasts with what is expected.",
      example: "Ingawa alikuwa mgonjwa, alikuja. (Bien qu'il/elle était malade, il/elle est venu(e).)",
      exampleEn: "Ingawa alikuwa mgonjwa, alikuja. (Although he/she was sick, he/she came.)"
    },
    'Hadi': {
      translation: "Jusqu'à ce que",
      translationEn: "Until",
      explanation: "'Hadi' exprime une limite temporelle — l'action se poursuit jusqu'à un événement.",
      explanationEn: "'Hadi' expresses a temporal limit — the action continues until an event occurs.",
      example: "Subiri hadi nirudi. (Attends jusqu'à ce que je revienne.)",
      exampleEn: "Subiri hadi nirudi. (Wait until I come back.)"
    },
  },

  'Adverbes': {
    'Sana': {
      translation: "Très, beaucoup",
      translationEn: "Very, a lot",
      explanation: "'Sana' est l'intensificateur le plus courant en swahili. Il se place après l'adjectif ou le verbe.",
      explanationEn: "'Sana' is the most common intensifier in Swahili. It is placed after the adjective or verb.",
      example: "Asante sana. (Merci beaucoup.) — Nyumba nzuri sana. (La maison est très belle.)",
      exampleEn: "Asante sana. (Thank you very much.) — Nyumba nzuri sana. (The house is very beautiful.)"
    },
    'Tu': {
      translation: "Seulement, juste",
      translationEn: "Only, just",
      explanation: "'Tu' est un restrictif qui limite la portée du terme précédent.",
      explanationEn: "'Tu' is a restrictive particle that limits the scope of the preceding term.",
      example: "Nilikuwa tu napita. (J'étais juste en train de passer.)",
      exampleEn: "Nilikuwa tu napita. (I was just passing by.)"
    },
    'Sasa': {
      translation: "Maintenant, tout de suite",
      translationEn: "Now, right away",
      explanation: "'Sasa' situe l'action dans le moment présent ou immédiat.",
      explanationEn: "'Sasa' places the action in the present or immediate moment.",
      example: "Njoo sasa! (Viens maintenant !)",
      exampleEn: "Njoo sasa! (Come now!)",
      audio: "Sw-sasa"
    },
    'Leo': {
      translation: "Aujourd'hui",
      translationEn: "Today",
      explanation: "Adverbe de temps situant l'action dans la journée en cours.",
      explanationEn: "Time adverb placing the action in the current day.",
      example: "Leo nitakwenda sokoni. (Aujourd'hui j'irai au marché.)",
      exampleEn: "Leo nitakwenda sokoni. (Today I will go to the market.)",
      audio: "Sw-leo"
    },
    'Jana': {
      translation: "Hier",
      translationEn: "Yesterday",
      explanation: "Adverbe de temps désignant le jour précédent.",
      explanationEn: "Time adverb designating the previous day.",
      example: "Alikuja jana. (Il/Elle est venu(e) hier.)",
      exampleEn: "Alikuja jana. (He/She came yesterday.)",
      audio: "Sw-jana"
    },
    'Kesho': {
      translation: "Demain",
      translationEn: "Tomorrow",
      explanation: "Adverbe de temps désignant le jour suivant.",
      explanationEn: "Time adverb designating the next day.",
      example: "Nitakwenda kesho. (J'irai demain.)",
      exampleEn: "Nitakwenda kesho. (I will go tomorrow.)",
      audio: "Sw-kesho"
    },
    'Polepole': {
      translation: "Doucement, peu à peu",
      translationEn: "Slowly, little by little",
      explanation: "Adverbe de manière exprimant la lenteur ou la progressivité.",
      explanationEn: "Manner adverb expressing slowness or gradualness.",
      example: "Jifunze polepole. (Apprends doucement, pas à pas.)",
      exampleEn: "Jifunze polepole. (Learn slowly, step by step.)"
    },
    'Tena': {
      translation: "Encore, de nouveau",
      translationEn: "Again",
      explanation: "'Tena' indique la répétition d'une action.",
      explanationEn: "'Tena' indicates the repetition of an action.",
      example: "Sema tena. (Dis-le encore une fois.)",
      exampleEn: "Sema tena. (Say it again.)"
    },
    'Kamwe': {
      translation: "Jamais",
      translationEn: "Never",
      explanation: "Adverbe de fréquence totalement négatif. S'utilise dans les phrases négatives.",
      explanationEn: "A wholly negative frequency adverb. Used in negative sentences.",
      example: "Sitafanya hivyo kamwe. (Je ne ferai jamais cela.)",
      exampleEn: "Sitafanya hivyo kamwe. (I will never do that.)"
    },
    'Labda': {
      translation: "Peut-être",
      translationEn: "Maybe, perhaps",
      explanation: "'Labda' exprime le doute ou la probabilité.",
      explanationEn: "'Labda' expresses doubt or probability.",
      example: "Labda atakuja kesho. (Il/Elle viendra peut-être demain.)",
      exampleEn: "Labda atakuja kesho. (He/She might come tomorrow.)"
    },
    'Tayari': {
      translation: "Déjà",
      translationEn: "Already",
      explanation: "'Tayari' indique qu'une action est accomplie avant le moment attendu.",
      explanationEn: "'Tayari' indicates that an action is completed before the expected moment.",
      example: "Nimeshamaliza tayari. (J'ai déjà fini.)",
      exampleEn: "Nimeshamaliza tayari. (I have already finished.)"
    },
    'Bado': {
      translation: "Encore, pas encore",
      translationEn: "Still, not yet",
      explanation: "'Bado' dans une phrase affirmative = encore. Dans une phrase négative = pas encore.",
      explanationEn: "'Bado' in an affirmative sentence = still. In a negative sentence = not yet.",
      example: "Bado ninasoma. (Je lis encore.) — Sijamaliza bado. (Je n'ai pas encore fini.)",
      exampleEn: "Bado ninasoma. (I am still reading.) — Sijamaliza bado. (I haven't finished yet.)"
    },
  },

};

export default grammaire;

export {};
